import { MARKETPLACE_CURRENCY_CODE } from "./marketplaceConstants";

// We're limiting the plethora of options available for formatting numbers in Intl.NumberFormat
// to a smaller, more manageable set that fits our use cases.
interface FormatNumberOptions {
  rounded?: Intl.NumberFormatOptions["roundingMode"];
  compact?: boolean;
  addPlus?: boolean;
  decimalPlaces?: number;
}

const formatNumberDefaultOptions: Required<FormatNumberOptions> = {
  rounded: "halfEven",
  compact: false,
  addPlus: false,
  decimalPlaces: 2,
};

export const formatNumber = (number: number | string | null | undefined, options: FormatNumberOptions = {}) => {
  const resolvedOptions = { ...formatNumberDefaultOptions, ...options };
  if (typeof number === "string") number = parseFloat(number);

  if (number === null || number === undefined || Number.isNaN(number)) return "-";

  const result = new Intl.NumberFormat(navigator.languages, {
    style: "decimal",

    roundingMode: resolvedOptions.rounded,
    maximumFractionDigits: resolvedOptions.decimalPlaces,
    minimumFractionDigits: resolvedOptions.decimalPlaces,
    signDisplay: resolvedOptions.addPlus ? "exceptZero" : "auto",
    notation: resolvedOptions.compact ? "compact" : "standard",
  }).format(number);

  if (result.includes("NaN")) {
    return "-";
  }

  return result;
};

export const formatCurrency = (
  number: number | string | null | undefined,
  options?: FormatNumberOptions & {
    /** You really shouldn't omit marketplace, but typing is a pain if it's not optional */
    marketPlace?: string;
  },
) => {
  const resolvedOptions = { marketPlace: "USA", ...formatNumberDefaultOptions, ...options };
  if (typeof number === "string") number = parseFloat(number);

  if (number === null || number === undefined || Number.isNaN(number)) return "-";

  const result = new Intl.NumberFormat(navigator.languages, {
    style: "currency",
    currency: MARKETPLACE_CURRENCY_CODE[resolvedOptions.marketPlace] ?? "USD",
    currencyDisplay: "narrowSymbol",

    roundingMode: resolvedOptions.rounded,
    maximumFractionDigits: resolvedOptions.decimalPlaces,
    minimumFractionDigits: resolvedOptions.decimalPlaces,
    signDisplay: resolvedOptions.addPlus ? "exceptZero" : "auto",
    notation: resolvedOptions.compact ? "compact" : "standard",
  }).format(number);

  if (result.includes("NaN")) {
    return "-";
  }

  return result;
};

export const formatPercent = (number: number | string | null | undefined, options: FormatNumberOptions = {}) => {
  const resolvedOptions = { ...formatNumberDefaultOptions, ...options };
  if (typeof number === "string") number = parseFloat(number);

  if (number === null || number === undefined || Number.isNaN(number)) return "-";

  const result = new Intl.NumberFormat(navigator.languages, {
    style: "percent",

    roundingMode: resolvedOptions.rounded,
    maximumFractionDigits: resolvedOptions.decimalPlaces,
    minimumFractionDigits: resolvedOptions.decimalPlaces,
    signDisplay: resolvedOptions.addPlus ? "exceptZero" : "auto",
    notation: resolvedOptions.compact ? "compact" : "standard",
  }).format(number);

  if (result.includes("NaN")) {
    return "-";
  }

  return result;
};

export function formatMetricValue(value: number | null | undefined, formatOptions: FormatOptions) {
  let displayValue = value?.toString();
  switch (formatOptions.format) {
    case "currency":
      displayValue = formatCurrency(value, formatOptions);
      break;
    case "percent":
      displayValue = formatPercent(value, formatOptions);
      break;
    case "number":
      displayValue = formatNumber(value, formatOptions);
      break;
  }
  return displayValue;
}
export type FormatOptions = FormatNumberOptions & { format: "number" | "percent" | "currency"; marketPlace?: string };

export const parseFormattedNumber = (number: number | string) => {
  if (typeof number === "number") {
    return number;
  }
  const parsedNumber = parseFloat(number.replaceAll(",", ""));

  return parsedNumber;
};

export const parseFormattedPercentage = (number: string) => {
  const parsedNumber = parseFloat(number.replaceAll("%,", ""));

  return parsedNumber;
};

export const parseFormattedCurrency = (number: string) => {
  const parsedCurrency = parseInt(number);

  return parsedCurrency;
};

// \p{Sc} rather than a symbol list, which would be a second currency list to
// keep in step with MARKETPLACE_CURRENCY_CODE.
const CELL_DECORATION = /[\s\u200B\u2060%\p{Sc}]/gu;
const NUMERIC_CELL = /^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/;

// Strict counterpart to parseFormattedNumber: null rather than NaN, and rejects
// the trailing garbage parseFloat accepts ("12abc" -> 12).
export const parseNumericCell = (raw: string | undefined | null): number | null => {
  if (raw == null) {
    return null;
  }

  const cleaned = raw.replaceAll(CELL_DECORATION, "");
  if (!NUMERIC_CELL.test(cleaned)) {
    return null;
  }

  // parseFloat("1,000") is 1, so separators go before the parse, not after.
  const parsed = Number(cleaned.replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : null;
};

export const getCurrencySymbol = (marketPlace: string | undefined) => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",

    currency: MARKETPLACE_CURRENCY_CODE[marketPlace as keyof typeof MARKETPLACE_CURRENCY_CODE] ?? "USD",
    currencyDisplay: "symbol",
  };
  const formatter = new Intl.NumberFormat(navigator.languages, options);
  // Use a number to get the formatted currency
  const parts = formatter.formatToParts(0);
  // Extract the symbol part
  const currencyPart = parts.find((part) => part.type === "currency");
  const currencySymbol = currencyPart ? currencyPart.value : "";
  return currencySymbol;
};
